import { INBOX_CONTACT_TYPE_OPTIONS, type InboxContactTypeValue } from '../../../shared/party-types.js';

export type InboxSortMode = 'smart' | 'newest';
export type InboxChannelFilter = 'email' | 'sms' | 'call' | 'portal_msg' | 'ai';
export type InboxDirectionFilter = 'all' | 'inbound' | 'outbound';
export type InboxReadStateFilter = 'all' | 'read' | 'unread';
export type InboxWorkStateFilter = 'all' | 'unresolved' | 'resolved';
export type InboxAssociationFilter = 'all' | 'needs_review' | 'linked';
export type InboxAttentionFilter = 'all' | 'neo_suggestions' | 'action_items';
export type DealConversationPreset = 'all_activity' | 'emails' | 'calls' | 'messages';
export type InboxFolder = 'inbox' | 'snoozed' | 'sent' | 'drafts' | 'spam';
export type InboxAccountPriorityFilter = 'all' | 'priority' | 'normal' | 'mute';
export type InboxScope = 'mine' | 'all';
// Values the contact-type filter can emit. Grouped chips send one representative
// value (Agents -> buyer_agent, Operators -> operator, Title Cos -> title_co);
// the server expands each to its full group. 'internal' is emitted by the Chats
// folder item, not by a contact chip.
export type InboxContactType =
  | 'all'
  | InboxContactTypeValue
  | 'internal'
  | 'ai_employee';

export interface InboxFilters {
  channels: InboxChannelFilter[];
  direction: InboxDirectionFilter;
  readState: InboxReadStateFilter;
  workState: InboxWorkStateFilter;
  association: InboxAssociationFilter;
  dealRelated: boolean;
  dealThreadId: string;
  dealPartyId: string;
  attention: InboxAttentionFilter;
  folder: InboxFolder;
  contactType: InboxContactType;
  connectedAccountId: string;
  accountPriority: InboxAccountPriorityFilter;
  // Thread-tag refinement (Task #104). Holds a tag id ('' = no tag filter); the
  // server resolves matching conversations via the shared conversation-key join.
  tag: string;
}

export type InboxFolderCountKey = InboxFolder | 'chats';

export interface InboxCounts {
  folders: Partial<Record<InboxFolderCountKey, number>>;
  contacts: Partial<Record<InboxContactType, number>>;
  // Per-channel counts within the current filter set, so the channel chips show numbers.
  channels?: Partial<Record<InboxChannelFilter, number>>;
}

export const DEFAULT_INBOX_SORT: InboxSortMode = 'smart';

export const DEFAULT_INBOX_FILTERS: InboxFilters = {
  channels: [],
  direction: 'inbound',
  readState: 'all',
  workState: 'unresolved',
  association: 'all',
  dealRelated: false,
  dealThreadId: '',
  dealPartyId: '',
  attention: 'all',
  folder: 'inbox',
  contactType: 'all',
  connectedAccountId: 'all',
  accountPriority: 'all',
  tag: '',
};

export function defaultDirectionForInboxFolder(folder: InboxFolder): InboxDirectionFilter {
  return folder === 'sent' || folder === 'drafts' ? 'outbound' : 'inbound';
}

// Standard inbox views for the context panel, in display order. 'inbox' is the
// default view (surfaced as the collapsible "Inboxes" account picker) and
// applies no server-side folder constraint, so it is not appended to the query
// and existing callers are unchanged. The Important + Follow Ups (starred) views
// were dropped: triage is now the 3-state control (Follow Up / No Attention
// Needed / Spam), so a non-spam message in the active queue IS the follow-up set.
export const STANDARD_FOLDERS: { value: InboxFolder; label: string }[] = [
  { value: 'inbox', label: 'Inboxes' },
  { value: 'snoozed', label: 'Snoozed' },
  { value: 'sent', label: 'Sent' },
  { value: 'drafts', label: 'Drafts' },
  { value: 'spam', label: 'Spam' },
];

// Contact-type filter options for the inbox context panel. Several chips are one
// role split across party types and send a representative value the server
// expands: Agents (buyer_agent) -> buyer's + listing agents; Operators (operator)
// -> operator/contractor/vendor; Title Cos (title_co) -> title org + attorney +
// agent. Wholesalers = the referral_partner / bird-dog party type. AI Employees
// carries the internal flag so the panel sets it apart at the bottom; the
// internal-team "Chats" view lives in the Folders section, not here.
export const CONTACT_TYPE_OPTIONS: { value: InboxContactType; label: string; internal?: boolean }[] = [
  { value: 'all', label: 'All Contacts' },
  ...INBOX_CONTACT_TYPE_OPTIONS,
];

// Conversational folders render as grouped threads (one row per conversation). The
// server only actually groups when the thread_key column exists, so requesting
// view=threads is safe before the migration: it returns the flat list, which the
// client renders unchanged (the thread rollup is simply absent).
const THREAD_GROUPING_FOLDERS: ReadonlySet<InboxFolder> = new Set([
  'inbox',
  'snoozed',
]);

export function folderSupportsThreadGrouping(folder: InboxFolder): boolean {
  return THREAD_GROUPING_FOLDERS.has(folder);
}

export function resolveInitialInboxSort(storedSort: string | null | undefined): InboxSortMode {
  return storedSort === 'newest' || storedSort === 'smart' ? storedSort : DEFAULT_INBOX_SORT;
}

export function inboxQueueSummaryParts(filters: InboxFilters, sortMode: InboxSortMode): string[] {
  const workStateLabel =
    filters.workState === 'unresolved'
      ? 'Follow Up'
      : filters.workState === 'resolved'
        ? 'No Attention Needed'
        : 'All States';
  const folderLabel = STANDARD_FOLDERS.find((folder) => folder.value === filters.folder)?.label ?? 'Inbox';
  const scopeLabel =
    filters.connectedAccountId === 'all' && filters.accountPriority === 'all' && filters.folder === 'inbox'
      ? 'All Inboxes'
      : filters.connectedAccountId !== 'all'
        ? 'Selected Inbox'
        : filters.accountPriority === 'priority'
          ? 'Priority Accounts'
          : filters.accountPriority === 'normal'
            ? 'Normal Accounts'
            : filters.accountPriority === 'mute'
              ? 'Muted Accounts'
              : folderLabel;
  const sortLabel = sortMode === 'smart' ? 'Smart Sort' : 'Newest First';
  return [workStateLabel, scopeLabel, sortLabel];
}

export function buildInboxQueryPath({
  limit,
  searchQuery,
  sortMode,
  filters,
  inboxScope,
  includeThreadView = true,
}: {
  limit: number;
  searchQuery: string;
  sortMode: InboxSortMode;
  filters: InboxFilters;
  inboxScope?: InboxScope;
  // The inbox list groups conversational folders into threads; universal search
  // passes false so it returns flat per-message results.
  includeThreadView?: boolean;
}) {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  const search = searchQuery.trim();
  if (search) params.set('q', search);
  params.set('sort', sortMode);
  for (const channel of filters.channels) params.append('channel', channel);
  if (filters.direction !== 'all') params.set('direction', filters.direction);
  if (filters.readState !== 'all') params.set('readState', filters.readState);
  if (filters.workState !== 'unresolved') params.set('workState', filters.workState);
  if (filters.association !== 'all') params.set('association', filters.association);
  if (filters.dealRelated) params.set('dealRelated', 'true');
  if (filters.dealThreadId) params.set('dealThreadId', filters.dealThreadId);
  if (filters.dealPartyId) params.set('dealPartyId', filters.dealPartyId);
  if (filters.attention !== 'all') params.set('attention', filters.attention);
  if (filters.folder !== 'inbox') params.set('folder', filters.folder);
  if (filters.contactType !== 'all') params.set('contactType', filters.contactType);
  if (filters.connectedAccountId !== 'all') params.set('connectedAccountId', filters.connectedAccountId);
  if (filters.accountPriority !== 'all') params.set('accountPriority', filters.accountPriority);
  if (filters.tag) params.set('tag', filters.tag);
  if (inboxScope) params.set('inboxScope', inboxScope);
  if (includeThreadView && folderSupportsThreadGrouping(filters.folder)) params.set('view', 'threads');
  return `/api/deal-messages/inbox?${params.toString()}`;
}

export function buildInboxCountsPath({
  searchQuery,
  filters,
  inboxScope,
  includeThreadView = true,
}: {
  searchQuery: string;
  filters: InboxFilters;
  inboxScope?: InboxScope;
  includeThreadView?: boolean;
}) {
  const params = new URLSearchParams();
  const search = searchQuery.trim();
  if (search) params.set('q', search);
  for (const channel of filters.channels) params.append('channel', channel);
  if (filters.direction !== 'all') params.set('direction', filters.direction);
  if (filters.readState !== 'all') params.set('readState', filters.readState);
  if (filters.workState !== 'unresolved') params.set('workState', filters.workState);
  if (filters.association !== 'all') params.set('association', filters.association);
  if (filters.dealRelated) params.set('dealRelated', 'true');
  if (filters.dealThreadId) params.set('dealThreadId', filters.dealThreadId);
  if (filters.dealPartyId) params.set('dealPartyId', filters.dealPartyId);
  if (filters.attention !== 'all') params.set('attention', filters.attention);
  if (filters.folder !== 'inbox') params.set('folder', filters.folder);
  if (filters.contactType !== 'all') params.set('contactType', filters.contactType);
  if (filters.connectedAccountId !== 'all') params.set('connectedAccountId', filters.connectedAccountId);
  if (filters.accountPriority !== 'all') params.set('accountPriority', filters.accountPriority);
  if (filters.tag) params.set('tag', filters.tag);
  if (inboxScope) params.set('inboxScope', inboxScope);
  if (includeThreadView && folderSupportsThreadGrouping(filters.folder)) params.set('view', 'threads');
  const query = params.toString();
  return query ? `/api/deal-messages/counts?${query}` : '/api/deal-messages/counts';
}

// Folder is a primary view selector (like Gmail's left nav), not a refinement
// chip, so it is intentionally excluded from the active-filter count.
export function activeInboxFilterCount(filters: InboxFilters) {
  return [
    filters.channels.length > 0,
    filters.direction !== defaultDirectionForInboxFolder(filters.folder),
    filters.readState !== 'all',
    filters.workState !== 'unresolved',
    filters.association !== 'all',
    filters.dealRelated || filters.dealThreadId !== '' || filters.dealPartyId !== '',
    filters.attention !== 'all',
    filters.contactType !== 'all',
    filters.connectedAccountId !== 'all',
    filters.accountPriority !== 'all',
    filters.tag !== '',
  ].filter(Boolean).length;
}

const CHANNEL_FILTER_LABELS: Record<InboxChannelFilter, string> = {
  email: 'Email',
  sms: 'SMS',
  call: 'Calls',
  portal_msg: 'Portal',
  ai: 'AI',
};

const ACCOUNT_PRIORITY_LABELS: Record<Exclude<InboxAccountPriorityFilter, 'all'>, string> = {
  priority: 'Priority accounts',
  normal: 'Normal accounts',
  mute: 'Muted accounts',
};

// One removable chip per active refinement. `clear` is merged into the current
// filters to drop just that refinement. Folder is excluded (a view selector,
// mirroring activeInboxFilterCount). Powers the inbox "active filters" row so every
// stacked dimension is visible + individually clearable.
export interface ActiveInboxFilterChip {
  key: string;
  label: string;
  clear: Partial<InboxFilters>;
}

export function describeActiveInboxFilters(
  filters: InboxFilters,
  // Resolves a tag id to its label so the tag chip reads as the tag name. Falls
  // back to a generic label when the vocabulary has not loaded yet.
  resolveTagLabel?: (tagId: string) => string | undefined,
  resolveConnectedAccountLabel?: (accountId: string) => string | undefined,
): ActiveInboxFilterChip[] {
  const chips: ActiveInboxFilterChip[] = [];
  for (const channel of filters.channels) {
    chips.push({
      key: `channel:${channel}`,
      label: CHANNEL_FILTER_LABELS[channel],
      clear: { channels: filters.channels.filter((candidate) => candidate !== channel) },
    });
  }
  if (filters.direction !== defaultDirectionForInboxFolder(filters.folder)) {
    const directionLabel = filters.direction === 'inbound'
      ? 'Inbound'
      : filters.direction === 'outbound'
        ? 'Outbound'
        : 'All directions';
    chips.push({
      key: 'direction',
      label: directionLabel,
      clear: { direction: defaultDirectionForInboxFolder(filters.folder) },
    });
  }
  if (filters.readState !== 'all') {
    chips.push({ key: 'readState', label: filters.readState === 'unread' ? 'Unread' : 'Read', clear: { readState: 'all' } });
  }
  if (filters.workState !== 'unresolved') {
    chips.push({ key: 'workState', label: filters.workState === 'resolved' ? 'No Attention Needed' : 'All states', clear: { workState: 'unresolved' } });
  }
  if (filters.association !== 'all') {
    chips.push({ key: 'association', label: filters.association === 'needs_review' ? 'Needs review' : 'Linked', clear: { association: 'all' } });
  }
  if (filters.dealRelated || filters.dealThreadId || filters.dealPartyId) {
    const label = filters.dealPartyId
      ? 'Deal Contacts: One contact'
      : filters.dealThreadId
        ? 'Deal Contacts: One deal'
        : 'Deal Contacts';
    chips.push({
      key: 'dealRelated',
      label,
      clear: { dealRelated: false, dealThreadId: '', dealPartyId: '' },
    });
  }
  if (filters.attention !== 'all') {
    chips.push({ key: 'attention', label: filters.attention === 'neo_suggestions' ? 'Neo suggestions' : 'Action items', clear: { attention: 'all' } });
  }
  if (filters.contactType !== 'all') {
    const label = filters.contactType === 'internal'
      ? 'Chats'
      : CONTACT_TYPE_OPTIONS.find((option) => option.value === filters.contactType)?.label ?? String(filters.contactType);
    chips.push({ key: 'contactType', label, clear: { contactType: 'all' } });
  }
  if (filters.connectedAccountId !== 'all') {
    chips.push({
      key: 'connectedAccountId',
      label: resolveConnectedAccountLabel?.(filters.connectedAccountId) ?? 'Selected inbox',
      clear: { connectedAccountId: 'all' },
    });
  }
  if (filters.accountPriority !== 'all') {
    chips.push({ key: 'accountPriority', label: ACCOUNT_PRIORITY_LABELS[filters.accountPriority], clear: { accountPriority: 'all' } });
  }
  if (filters.tag) {
    chips.push({ key: 'tag', label: resolveTagLabel?.(filters.tag) ?? 'Tag', clear: { tag: '' } });
  }
  return chips;
}

// Clear every refinement back to defaults while keeping the current folder view.
export function resetInboxFilters(filters: InboxFilters): InboxFilters {
  return {
    ...DEFAULT_INBOX_FILTERS,
    folder: filters.folder,
    direction: defaultDirectionForInboxFolder(filters.folder),
  };
}

const DEAL_CONVERSATION_CHANNELS: Record<DealConversationPreset, InboxChannelFilter[]> = {
  all_activity: [],
  emails: ['email'],
  calls: ['call'],
  messages: ['sms', 'portal_msg'],
};

export function applyDealConversationPreset(
  filters: InboxFilters,
  preset: DealConversationPreset,
): InboxFilters {
  return {
    ...filters,
    association: 'all',
    dealRelated: true,
    channels: [...DEAL_CONVERSATION_CHANNELS[preset]],
  };
}

export function isDealConversationPresetActive(
  filters: InboxFilters,
  preset: DealConversationPreset,
): boolean {
  if (!filters.dealRelated) return false;
  const expected = DEAL_CONVERSATION_CHANNELS[preset];
  return filters.channels.length === expected.length
    && expected.every((channel) => filters.channels.includes(channel));
}

// Saved inbox segments persist a filter combo in the shared `saved_views` table
// (scope 'inbox'). The stored `filters` payload is jsonb authored by this app,
// but it may predate a newer filter dimension or be hand-edited, so recall
// merges it over the current defaults: missing keys fall back to default (a
// segment saved before a dimension existed just leaves that dimension at 'all'),
// and a malformed/non-object payload yields a clean default set. Never throws.
export function deserializeInboxSegmentFilters(raw: unknown): InboxFilters {
  // Fresh channels array every call: spreading DEFAULT_INBOX_FILTERS is a shallow
  // copy, so the channels array would otherwise alias the module-level default.
  const defaults: InboxFilters = { ...DEFAULT_INBOX_FILTERS, channels: [...DEFAULT_INBOX_FILTERS.channels] };
  if (!raw || typeof raw !== 'object') return defaults;
  const stored = raw as Partial<InboxFilters>;
  const storedFolder = stored.folder ?? defaults.folder;
  const dealThreadId = typeof stored.dealThreadId === 'string' ? stored.dealThreadId : '';
  const dealPartyId = typeof stored.dealPartyId === 'string' ? stored.dealPartyId : '';
  return {
    ...defaults,
    ...stored,
    dealRelated: stored.dealRelated === true || dealThreadId !== '' || dealPartyId !== '',
    dealThreadId,
    dealPartyId,
    direction: stored.direction ?? defaultDirectionForInboxFolder(storedFolder),
    // channels must be an array of channel values; guard a non-array stored value.
    channels: Array.isArray(stored.channels) ? [...stored.channels] : defaults.channels,
  };
}
